import { memo, useCallback, useMemo } from "react";
import { ScrollView, View, Text, Image, Pressable, StyleSheet, Linking } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { FontAwesomeIcon, tabIconSize, screenWidth, logError, os } from "../../platform";
import { faLocationDot, faPhone, faEnvelope, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { STRINGS } from "../../constants/strings";
import { COLORS } from "../../theme/colors";
import AppStore from "../../storage/appStore";

const parkfinderIcon = require("../../assets/parkfinder_icon.png");

/**
 * Determines if the screen width is wide enough for multi-column layout.
 * Uses 750px as the breakpoint for switching to two-column grid layout.
 * @type {boolean}
 */
const isWide = screenWidth > 750;

/**
 * Width for individual place items on wide screens.
 * @type {number}
 */
const ITEM_WIDTH_WIDE = 325;

const openInWebView = async (url, name, navigation, onNavigate) => {
  if (onNavigate) {
    onNavigate();
  }

  // Standard handling for non-park facilities or web platform
  if (os === "web") {
    await Linking.openURL(url);
  } else {
    navigation.navigate("WebViewScreen", { url: url, title: name });
  }
};

// ---------------------------------------------------------------------------
// Shared hooks
// ---------------------------------------------------------------------------

/**
 * Returns a memoized handler that opens a URL via Linking, logging errors and
 * setting an error message on failure.
 * For parks: tries to open VB Park Finder app via deep link with park name, falls back to WebViewScreen.
 * For other facilities: On web platform, opens in external browser. On native platforms, opens in WebViewScreen.
 * @param {string|null} url - URL to open
 * @param {string} name - Name of the place (used for error logging)
 * @param {Object} navigation - React Navigation object for navigating to WebViewScreen
 * @param {Function} onNavigate - Optional callback to call before navigation (e.g., to close modal)
 * @param {Function} setErrorMessage - Store setter for displaying error messages
 * @param {boolean} [isParkUrl=false] - Whether this URL should use park-specific deep linking
 * @returns {Function} Async press handler
 */
const useLinkHandler = (url, name, navigation, onNavigate, setErrorMessage, isParkUrl = false) =>
  useCallback(async () => {
    if (!url) return;

    try {
      // Special handling for parks on native platforms
      if (isParkUrl && url.includes("parkfinder") && os !== "web") {
        try {
          // Try to open VB Park Finder app with deep link to specific park
          // Format: vbparkfinder://details/{park_name}
          const parkDeepLink = `${STRINGS.vbparkfinderNativeLink}details/${encodeURIComponent(name)}`;
          await Linking.openURL(parkDeepLink);
          return;
        } catch (appError) {
          // If app is not installed, fall through to openInWebView
        }
      }

      await openInWebView(url, name, navigation, onNavigate);
    } catch (error) {
      logError("url_link_failed", {
        message: error.message,
        stack: error.stack,
        url,
      });
      setErrorMessage(STRINGS.failedToOpenLink);
    }
  }, [url, name, navigation, onNavigate, setErrorMessage, isParkUrl]);

/**
 * Returns a memoized handler that opens a URL via Linking, logging errors and
 * setting an error message on failure.
 * @param {string|null} url - URL to open
 * @param {string} errorEvent - Analytics event name for error logging
 * @param {Object} errorMeta - Extra metadata to include in the error log
 * @param {Function} setErrorMessage - Store setter for displaying error messages
 * @returns {Function} Async press handler
 */
const usePhoneOrEmailHandler = (url, errorEvent, errorMeta, setErrorMessage) =>
  useCallback(async () => {
    if (!url) return;
    try {
      await Linking.openURL(url);
    } catch (error) {
      logError(errorEvent, { message: error.message, stack: error.stack, ...errorMeta });
      setErrorMessage(STRINGS.failedToOpenLink);
    }
  }, [url, errorEvent, errorMeta, setErrorMessage]);

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

/**
 * Renders the address row: location icon + address text + optional drive info.
 * Wrapped in a Pressable that calls onPress.
 */
const AddressRow = memo(({ name, address, driveTime, driveDistance, onPress }) => (
  <Pressable
    onPress={onPress}
    testID={`location-button-${name}`}
    accessible={true}
    accessibilityRole="button"
    accessibilityLabel={`View ${name}`}
    accessibilityHint={`Opens link to ${name}`}
  >
    <View style={styles.groupItemSubheaderContainer}>
      <FontAwesomeIcon icon={faLocationDot} size={tabIconSize} color={COLORS.red} />
      <View style={styles.groupItemTextContainer}>
        <Text style={styles.groupItemSubheader} maxFontSizeMultiplier={1.2}>
          {address}
        </Text>
        {driveTime && (
          <Text style={styles.groupItemDriveInfo} maxFontSizeMultiplier={1.2}>
            Drive Time: {driveTime}
          </Text>
        )}
        {driveDistance && (
          <Text style={styles.groupItemDriveInfo} maxFontSizeMultiplier={1.2}>
            Drive Distance: {driveDistance}
          </Text>
        )}
      </View>
    </View>
  </Pressable>
));

/**
 * Renders a phone number — pressable on native, plain text on web.
 */
const PhoneRow = memo(({ phone, onPress }) => {
  if (!phone) return null;

  if (os === "ios" || os === "android") {
    return (
      <Pressable
        onPress={onPress}
        style={styles.phoneButton}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={`Call ${phone}`}
        accessibilityHint="Opens phone dialer"
      >
        <View style={styles.phoneContainer}>
          <FontAwesomeIcon icon={faPhone} size={14} color={COLORS.link || COLORS.red} />
          <Text style={styles.groupItemPhone} maxFontSizeMultiplier={1.2}>
            {phone}
          </Text>
        </View>
      </Pressable>
    );
  }

  if (os === "web") {
    return (
      <View style={styles.webPhoneContainer}>
        <Text style={styles.groupItemContact} maxFontSizeMultiplier={1.2}>
          {phone}
        </Text>
      </View>
    );
  }

  return null;
});

/**
 * Renders a pressable email row with envelope icon.
 */
const EmailRow = memo(({ email, onPress }) => {
  if (!email) return null;
  return (
    <Pressable
      onPress={onPress}
      style={styles.emailButton}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={`Email ${email}`}
      accessibilityHint="Opens email client"
    >
      <View style={styles.emailContainer}>
        <FontAwesomeIcon icon={faEnvelope} size={14} color={COLORS.link || COLORS.red} />
        <Text style={styles.groupItemEmail} maxFontSizeMultiplier={1.2}>
          {email}
        </Text>
      </View>
    </Pressable>
  );
});

/**
 * Renders a pressable URL row with globe icon.
 * @param {string} label - Display text for the link
 */
const UrlRow = memo(({ url, label, name, onPress }) => {
  if (!url) return null;
  return (
    <Pressable
      onPress={onPress}
      style={styles.urlButton}
      accessible={true}
      accessibilityRole="button"
      accessibilityLabel={`Open ${name} website`}
      accessibilityHint="Opens website in browser"
    >
      <View style={styles.urlContainer}>
        {url.includes("parkfinder") ? (
          <>
            <Image source={parkfinderIcon} style={styles.image} resizeMode="contain" />
            <Text style={styles.groupItemUrl} maxFontSizeMultiplier={1.2} numberOfLines={1}>
              {STRINGS.vbparkfinderAppName}
            </Text>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faGlobe} size={14} color={COLORS.link || COLORS.red} />
            <Text style={styles.groupItemUrl} maxFontSizeMultiplier={1.2} numberOfLines={1}>
              {label}
            </Text>
          </>
        )}
      </View>
    </Pressable>
  );
});

// ---------------------------------------------------------------------------
// Place item components
// ---------------------------------------------------------------------------

/**
 * Renders a single nearby place item with location details and navigation link.
 * Displays place name, address, optional drive time/distance, phone number, email, and URL.
 * Clicking the item opens the location in an external map application.
 * The component generates a testID using the format "location-button-{place.name}" for testing purposes.
 * @param {Object} props - Component props
 * @param {string} props.type - Type of place (e.g., "school", "park") for link labeling
 * @param {Object} props.place - Place data object
 * @param {string} props.place.name - Name of the place (used in testID as "location-button-{name}")
 * @param {string} props.place.address - Address of the place
 * @param {string} [props.type] - Optional type of place (e.g., "school", "park", "library")
 * @param {Object} [props.place.location] - Optional location coordinates object
 * @param {string} [props.place.url] - Optional URL to the place website
 * @param {string} [props.place.driveTime] - Optional drive time to the place
 * @param {string} [props.place.driveDistance] - Optional drive distance to the place
 * @param {string} [props.place.phone] - Optional phone number
 * @param {string} [props.place.email] - Optional email address
 * @param {boolean} props.isLastRowItem - Whether this item is in the last row (affects border styling)
 * @returns {JSX.Element}
 */
const NearbyPlaceItem = memo(({ place, type, isLastRowItem, onNavigate }) => {
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const navigation = useNavigation();

  const addressUrl = useMemo(() => {
    if ((type === "schools" || type === "parks") && place?.name) {
      return `${STRINGS.googleMapLink}${place?.name} ${place?.address}`;
    }
    return place.url || `${STRINGS.googleMapLink}${place.name} ${place?.address}`;
  }, [type, place?.name, place?.address, place.url]);

  const phoneUrl = useMemo(() => (place.phone ? `tel:${place.phone.replace(/[^0-9]/g, "")}` : null), [place.phone]);

  const emailUrl = useMemo(() => (place.email ? `mailto:${place.email}` : null), [place.email]);

  const urlLabel = useMemo(() => {
    if (!place.url) return null;
    if (place.url.includes("google.com/maps")) return "Google Maps";
    if (type === "schools") return "School Website";
    if (type === "parks") return "Park Website";
    return place.url;
  }, [place.url, type]);

  const handleAddressPress = useLinkHandler(addressUrl, place.name, navigation, onNavigate, setErrorMessage);
  const handlePhonePress = usePhoneOrEmailHandler(
    phoneUrl,
    "phone_call_failed",
    { phone: place.phone },
    setErrorMessage
  );
  const handleEmailPress = usePhoneOrEmailHandler(
    emailUrl,
    "email_link_failed",
    { email: place.email },
    setErrorMessage
  );
  const handleUrlPress = useLinkHandler(place.url, place.name, navigation, onNavigate, setErrorMessage, true);

  return (
    <View style={[styles.groupItem, isLastRowItem ? styles.groupItemNoBorder : null]}>
      <Text style={styles.groupItemHeader} maxFontSizeMultiplier={1.2}>
        {place.name}
      </Text>
      <View style={styles.groupItemButton}>
        <AddressRow
          name={place.name}
          address={place.address}
          driveTime={place.driveTime}
          driveDistance={place.driveDistance}
          onPress={handleAddressPress}
        />
        <PhoneRow phone={place.phone} onPress={handlePhonePress} />
        <EmailRow email={place.email} onPress={handleEmailPress} />
        <UrlRow url={place.url} label={urlLabel} name={place.name} onPress={handleUrlPress} />
      </View>
    </View>
  );
});

/**
 * Renders a polling location item with precinct details, drive info, phone, URL, and an info note.
 * @param {Object} props - Component props
 * @param {Object} props.place - Polling location data object
 * @param {string} props.place.locationName - Name of the polling location
 * @param {string} props.place.address - Address of the polling location
 * @param {string} [props.place.phone] - Phone number (757- prefix will be prepended)
 * @param {string} [props.place.driveTime] - Drive time to the location
 * @param {string} [props.place.driveDistance] - Drive distance to the location
 * @param {string} [props.place.url] - Google Maps URL for the location
 * @param {string} props.district - Election district label
 * @param {boolean} props.isLastRowItem - Whether this item is in the last row (affects border styling)
 * @returns {JSX.Element}
 */
const PollingLocationItem = memo(({ place, district, isLastRowItem, onNavigate }) => {
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const navigation = useNavigation();

  const fullPhone = useMemo(() => (place.phone ? `757-${place.phone}` : null), [place.phone]);

  const addressUrl = useMemo(
    () => place.url || `${STRINGS.googleMapLink}${encodeURIComponent(place.locationName)}`,
    [place.url, place.locationName]
  );

  const phoneUrl = useMemo(() => (fullPhone ? `tel:${fullPhone.replace(/[^0-9]/g, "")}` : null), [fullPhone]);

  const handleAddressPress = useLinkHandler(addressUrl, place.locationName, navigation, onNavigate, setErrorMessage);
  const handlePhonePress = usePhoneOrEmailHandler(
    phoneUrl,
    "polling_phone_call_failed",
    { phone: fullPhone },
    setErrorMessage
  );
  const handleUrlPress = useLinkHandler(place.url, place.locationName, navigation, onNavigate, setErrorMessage);

  return (
    <View style={[styles.groupItem, isLastRowItem ? styles.groupItemNoBorder : null]}>
      <Text style={styles.groupItemHeader} maxFontSizeMultiplier={1.2}>
        {`${STRINGS.votingInfo.pollingLocation}: ${place.locationName}`}
      </Text>
      <View style={styles.groupItemButton}>
        <AddressRow
          name={place.locationName}
          address={place.address}
          driveTime={place.driveTime}
          driveDistance={place.driveDistance}
          onPress={handleAddressPress}
        />
        <PhoneRow phone={fullPhone} onPress={handlePhonePress} />
        <UrlRow url={place.url} label="Google Maps" name={place.locationName} onPress={handleUrlPress} />

        <View style={styles.pollingInfoContainer}>
          <Text style={styles.pollingInfoNote} maxFontSizeMultiplier={1.2}>
            {`${STRINGS.votingInfo.precinct}: ${place.precinctNumber}`}
          </Text>
          <Text style={styles.pollingInfoNote} maxFontSizeMultiplier={1.2}>
            {`${STRINGS.votingInfo.electionDistrict}: ${district.replace("District", "").trim()}`}
          </Text>
          <Text style={styles.pollingInfoNote} maxFontSizeMultiplier={1.2}>
            {`${STRINGS.votingInfo.houseDistrict}: ${place.houseDistrict}`}
          </Text>
          <Text style={styles.pollingInfoNote} maxFontSizeMultiplier={1.2}>
            {`${STRINGS.votingInfo.senateDistrict}: ${place.senateDistrict}`}
          </Text>
        </View>
      </View>
    </View>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Displays a scrollable list of nearby places with location information and navigation links.
 * Supports both array and object data formats, with responsive multi-column layout on wide screens.
 * Handles parks, fire stations, police stations, libraries (array format) and schools (object format).
 * Also supports polling location data (object with locationName and precinct fields).
 * @param {Object} props - Component props
 * @param {string} props.title - Section title to display in the header
 * @param {string} props.type - Type of places being displayed (e.g., "parks", "schools") for link labeling
 * @param {Array<Object>|Object} props.nearbyPlaces - Array or object of nearby place data
 * @param {string} [props.district] - Election district label (polling locations only)
 * @param {Object} [props.icon] - Optional FontAwesome icon to display in the header
 * @returns {JSX.Element}
 */
const NearbyPlaces = memo(({ title, type, nearbyPlaces, district, icon, onNavigate }) => {
  const isLastRowItem = useCallback(
    (index) => {
      if (!isWide) return index === nearbyPlaces?.length - 1;

      const itemsPerRow = 2;
      const totalRows = Math.ceil(nearbyPlaces?.length / itemsPerRow);
      const itemRow = Math.floor(index / itemsPerRow);
      return itemRow === totalRows - 1;
    },
    [nearbyPlaces?.length]
  );

  // Detect polling location data shape (has locationName and precinct fields)
  const isPollingLocation = useMemo(
    () =>
      !Array.isArray(nearbyPlaces) &&
      typeof nearbyPlaces === "object" &&
      nearbyPlaces !== null &&
      "locationName" in nearbyPlaces,
    [nearbyPlaces]
  );

  /**
   * Renders the appropriate place items based on the data structure.
   * Handles arrays, polling locations, and object-based school data.
   */
  const renderPlaces = useCallback(() => {
    if (Array.isArray(nearbyPlaces) && nearbyPlaces.length > 0) {
      return nearbyPlaces.map((place, index) => (
        <NearbyPlaceItem
          key={`${place.name}-${index}`}
          type={type}
          place={place}
          isLastRowItem={isLastRowItem(index)}
          onNavigate={onNavigate}
        />
      ));
    }

    if (isPollingLocation) {
      return <PollingLocationItem place={nearbyPlaces} district={district} isLastRowItem={true} onNavigate={onNavigate} />;
    }

    if (typeof nearbyPlaces === "object" && nearbyPlaces !== null) {
      return Object.entries(nearbyPlaces).map(([key, place], index) => {
        if (!place) return null;
        return (
          <View
            key={`${key}-${index}`}
            style={[
              styles.groupItem,
              index === Object.keys(nearbyPlaces).length - 1 ? styles.groupItemNoBorder : null,
            ]}
          >
            <Text style={styles.schoolTypeHeader} maxFontSizeMultiplier={1.2}>
              {key.charAt(0).toUpperCase() + key.slice(1)} School
            </Text>
            <NearbyPlaceItem place={place} type={type} isLastRowItem={true} onNavigate={onNavigate} />
          </View>
        );
      });
    }

    return null;
  }, [nearbyPlaces, type, isLastRowItem, onNavigate, isPollingLocation, district]);

  const places = renderPlaces();
  if (!places) return null;

  return (
    <ScrollView style={styles.container} testID="closest-facilities-container">
      <View style={styles.headerContainer}>
        {icon?.icon && <FontAwesomeIcon icon={icon.icon} size={tabIconSize} color={icon.color} />}
        {icon?.image && <Image source={icon.image} style={icon.style} resizeMode="contain" />}
        <Text style={styles.headerText} maxFontSizeMultiplier={1.2}>
          {title}
        </Text>
      </View>
      <View style={styles.groupContainer} testID="facilities-list">
        {places}
      </View>
    </ScrollView>
  );
});

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

/**
 * StyleSheet for NearbyPlaces component and its sub-components.
 */
const styles = StyleSheet.create({
  container: {
    marginTop: 20,
  },
  headerContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 15,
    marginBottom: 10,
    alignItems: "center",
  },
  headerText: {
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: 5,
  },
  groupContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  groupItem: {
    flexDirection: "column",
    gap: 10,
    borderBottomWidth: 1,
    borderColor: COLORS.border,
    width: screenWidth >= 750 ? ITEM_WIDTH_WIDE : "100%",
    paddingBottom: 15,
  },
  groupItemNoBorder: {
    borderBottomWidth: 0,
    paddingBottom: 0,
  },
  groupItemHeader: {
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 10,
  },
  image: {
    width: 30,
    height: 30,
  },
  schoolTypeHeader: {
    fontSize: 16,
    fontWeight: "bold",
    color: COLORS.textSecondary,
  },
  groupItemSubheaderContainer: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },
  groupItemTextContainer: {
    flexDirection: "column",
    gap: 4,
    flex: 1,
  },
  groupItemSubheader: {
    fontSize: 14,
    fontWeight: "700",
    textDecorationLine: os === "web" ? "underline" : "none",
  },
  groupItemDriveInfo: {
    fontSize: 13,
    color: COLORS.textSecondary,
  },
  groupItemContact: {
    fontSize: 13,
    color: COLORS.textSecondary,
  },
  webPhoneContainer: {
    marginLeft: 34,
    marginVertical: 12,
  },
  phoneButton: {
    marginVertical: 12,
    marginLeft: 34,
    justifyContent: "center",
  },
  phoneContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  groupItemPhone: {
    fontSize: 15,
    color: COLORS.link || COLORS.red,
    textDecorationLine: os === "web" ? "underline" : "none",
    fontWeight: "600",
  },
  urlButton: {
    marginVertical: 12,
    marginLeft: 34,
    justifyContent: "center",
  },
  urlContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  groupItemUrl: {
    fontSize: 15,
    color: COLORS.link || COLORS.red,
    textDecorationLine: os === "web" ? "underline" : "none",
    fontWeight: "600",
  },
  emailButton: {
    marginVertical: 2,
    marginLeft: 34,
    justifyContent: "center",
  },
  emailContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  groupItemEmail: {
    fontSize: 15,
    color: COLORS.link || COLORS.red,
    textDecorationLine: os === "web" ? "underline" : "none",
    fontWeight: "600",
  },
  groupItemButton: {
    padding: 8,
  },
  pollingInfoContainer: {
    flexDirection: "column",
  },
  pollingInfoNote: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 10,
    marginLeft: 34,
    fontStyle: "italic",
  },
});

export default NearbyPlaces;
